import React, { useCallback, useContext, useState } from 'react';
import { Button } from '@material-ui/core';
import { ETables } from '../../types/enums/ETables';
import useCellStatusStyleHandler from '../../utils/useCellStatusStyleHandler';
import useColumnsHeaders from '../../utils/useColumnsHeaders';
import { NSCruiseShips } from '../../types/interfaces/NSCruiseShips';
import { SessionContext } from '../../contexts/session/SessionContext';
import { useTranslation } from 'react-i18next';
import tPaths from '../../utils/tPaths';
import useApi from '../../utils/useApi';
import { endpoints } from '../../utils/endpoints';
import useSnackbar from '../../utils/useSnackbar';
import CruiseShipsTableWithState from './CruiseShipsTableWithState';
import AddShipDialog from './components/AddShipDialog';
import { EStatus } from '../../types/enums/EStatus';

const tableName = ETables.CruiseShips;

const joinNames = (items: NSCruiseShips.IRelatedItem[] = []) =>
  items
    .map((item) => item.name)
    .filter(Boolean)
    .join(', ') || '-';

const CruiseShips = () => {
  const cellStyleHandler = useCellStatusStyleHandler();
  const { permissions } = useContext(SessionContext);
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { patch } = useApi(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleDeactivate = useCallback(
    (id: string, event: React.MouseEvent) => {
      event.stopPropagation();
      patch({
        endpoint: endpoints.cruiseShip(id),
        body: { status: 'Inactive' },
        onSuccess: () => {
          snackbar({ severity: 'success', message: t(tPaths.table.successOnDeactivate) });
          setRefreshKey((k) => k + 1);
        },
      });
    },
    [patch, snackbar, t],
  );

  const dataFormatter = useCallback(
    (dataTable: NSCruiseShips.ITableItem[]): NSCruiseShips.IItemDataFormatted[] =>
      dataTable.map((table) => {
        const statusValue = table.status?.current || '';
        const isActive = statusValue === EStatus.Active;

        return {
          _id: table._id,
          name: table.name,
          cruiseLines: joinNames(table.cruiseLines),
          packages: joinNames(table.packages),
          status: statusValue,
          action: () =>
            isActive && permissions.packages.edit ? (
              <Button size="small" color="secondary" onClick={(e) => handleDeactivate(table._id, e)}>
                {t(tPaths.table.deactivate)}
              </Button>
            ) : null,
        };
      }),
    [handleDeactivate, permissions.packages.edit, t],
  );

  const columns = useColumnsHeaders([
    { accessorAsHeader: 'name' },
    { accessorAsHeader: 'cruiseLines' },
    { accessorAsHeader: 'packages' },
    { accessorAsHeader: 'status' },
    { accessorAsHeader: 'action' },
  ]);

  return (
    <>
      <CruiseShipsTableWithState
        tableName={tableName}
        dataFormatter={dataFormatter}
        cellStyleHandler={cellStyleHandler}
        columns={columns}
        exportable={true}
        refreshable={true}
        addable={permissions.packages.edit}
        onAddClick={() => setIsAddDialogOpen(true)}
        refreshTrigger={refreshKey}
      />
      <AddShipDialog
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onSuccess={() => setRefreshKey((k) => k + 1)}
      />
    </>
  );
};

export default CruiseShips;
